// Perfis de convite por setor. Um "perfil" é uma abstração amigável sobre
// (papel + áreas) do RBAC: ao convidar alguém, o admin escolhe um perfil e o
// sistema aplica o papel e as áreas corretas. Mais de uma pessoa pode ter o
// mesmo perfil. Quando uma etapa tem várias áreas responsáveis, qualquer
// usuário de qualquer uma dessas áreas pode caminhar com ela (ver can_edit_stage).
import type { AppRole } from './database.types';

export interface Profile {
  key: string;
  label: string;
  role: AppRole;
  areaKeys: string[];
  hint: string;
}

// Observação: "Comercial" mapeia para a área interna 'vendas' (a chave é
// mantida por compatibilidade; o rótulo exibido é "Comercial").
export const PROFILES: Profile[] = [
  { key: 'marketing',    label: 'Marketing',    role: 'editor', areaKeys: ['marketing'],    hint: 'Briefing, estratégia, preço e lançamento.' },
  { key: 'pd',           label: 'P&D',          role: 'editor', areaKeys: ['pd'],           hint: 'Formulação e desenvolvimento do produto.' },
  { key: 'comercial',    label: 'Comercial',    role: 'editor', areaKeys: ['vendas'],       hint: 'Análise de mercado, preço e volume.' },
  { key: 'custos',       label: 'Custos',       role: 'editor', areaKeys: ['custos'],       hint: 'Viabilidade financeira e CPV.' },
  { key: 'producao',     label: 'Produção',     role: 'editor', areaKeys: ['producao'],     hint: 'Viabilidade de processo e testes em planta.' },
  { key: 'regulatorios', label: 'Regulatórios', role: 'editor', areaKeys: ['regulatorios'], hint: 'Avaliação regulatória, cadastro e rótulo.' },
  { key: 'suprimentos',  label: 'Suprimentos',  role: 'editor', areaKeys: ['suprimentos'],  hint: 'Compras, bonificações e cobertura.' },
  { key: 'embalagens',   label: 'Embalagens',   role: 'editor', areaKeys: ['embalagens'],   hint: 'Desenvolvimento e compra de embalagens.' },
  { key: 'qualidade',    label: 'Qualidade',    role: 'editor', areaKeys: ['qualidade'],    hint: 'Garantia da qualidade; pode abrir novos projetos.' },
  { key: 'visitante',    label: 'Visitante (vê tudo, não edita)', role: 'viewer', areaKeys: [], hint: 'Acesso somente leitura a todo o painel.' },
  { key: 'admin',        label: 'Admin (edita tudo e gerencia membros)', role: 'admin', areaKeys: [], hint: 'Controle total e gestão de usuários.' },
];

export const PROFILE_BY_KEY: Record<string, Profile> = Object.fromEntries(
  PROFILES.map((p) => [p.key, p]),
);

/**
 * Deriva o perfil (para exibição) a partir do papel + áreas de um usuário.
 * Usuários "gestor" (PMO) e combinações fora do padrão retornam null → a tela
 * mostra o papel/áreas crus.
 */
export function profileFor(role: AppRole, areaKeys: string[]): Profile | null {
  if (role === 'admin') return PROFILE_BY_KEY.admin ?? null;
  if (role === 'viewer') return PROFILE_BY_KEY.visitante ?? null;
  if (role === 'editor' && areaKeys.length === 1) {
    return PROFILES.find((p) => p.role === 'editor' && p.areaKeys[0] === areaKeys[0]) ?? null;
  }
  return null;
}
